import { useSessionStorage } from "./useSessionStorage";
import { useState } from "react";
import actions from "../config.json";
import { getExperienceParams } from "../utils/experienceContext";

export const usePassions = (experienceType) => {
  const storageKey = "storage";
  const { setItem, getItem } = useSessionStorage(experienceType, {
    key: storageKey,
    value: {
      golf: 0,
      beach: 0,
      ski: 0,
      culinary: 0,
      culture: 0,
      music: 0,
      spa: 0,
    },
  });
  const [passions, setPassions] = useState(getItem(storageKey));

  const selectPassion = (passion) => {
    const value = { ...getItem(storageKey) };
    value[passion] = 1;
    setItem(storageKey, value);
    setPassions(value);
  };

  const tapPassion = (passion, maxTaps = 2, val) => {
    const value = { ...getItem(storageKey) };
    let taps = value[passion] || 0;

    if (taps >= maxTaps) {
      taps = val ?? 0;
    } else {
      taps += val ?? 1;
    }

    value[passion] = taps;
    setItem(storageKey, value);
    setPassions(value);
    return taps;
  };

  const getPassionValue = (passion) => {
    return getItem(storageKey)[passion] ?? 0;
  };

  const unselectPassion = (passion) => {
    const value = { ...getItem(storageKey) };
    value[passion] = 0;
    setItem(storageKey, value);
    setPassions(value);
  };

  const dislikePassion = (passion) => {
    const value = { ...getItem(storageKey) };
    value[passion] = -1;
    setItem(storageKey, value);
    setPassions(value);
  };

  const isPassionSelected = (passion) => {
    return (passions?.[passion] || 0) > 0;
  };

  const isAnyPassionSelected = () => {
    return Object.entries(passions).find(([, value]) => value > 0) != null;
  };

  const getSelectedPassionsAsArray = () => {
    const passions = getItem(storageKey);
    return Object.entries(passions)
      .filter(([, value]) => value > 0)
      .map(([key, value]) => {
        if (key === "golf" || key === "spa") {
          return value >= 2 ? `${key}-love` : `${key}-like`;
        }
        return key;
      });
  };

  const getSelectedPassionsAsFlatArray = () => {
    const passions = getItem(storageKey);
    return Object.entries(passions)
      .filter(([, value]) => value > 0)
      .map(([key]) => key);
  };

  const savePassionsToServer = async (acid) => {
    const passions = getItem(storageKey);

    const data = [
      {
        category: "GOLF_LOV",
        values: [],
        dislike: false,
      },
      {
        category: "SPA_LOV",
        values: [],
        dislike: false,
      },
      {
        category: "INTERESTS_AND_HOBBIES_LOV",
        values: [],
        dislikes: [],
      },
    ];

    Object.entries(passions).forEach(([key, value]) => {
      if (value > 0) {
        switch (key) {
          case "golf":
            if (value > 1) {
              data[0].values.push("ENTHU");
            } else {
              data[0].values.push("CASUAL");
            }
            break;
          case "spa":
            if (value > 1) {
              data[1].values.push("ENTHU");
            } else {
              data[1].values.push("CASUAL");
            }
            break;
          case "beach":
            data[2].values.push("BCH");
            break;
          case "ski":
            data[2].values.push("WNTRACT");
            break;
          case "culinary":
            data[2].values.push("COOK");
            break;
          case "culture":
            data[2].values.push("CULTR");
            break;
          case "music":
            data[2].values.push("MUSIC");
            break;
          default:
        }
      }

      if (value < 0) {
        // explicit dislikes – only Video experience sets this for now (MPP-1692)
        switch (key) {
          case "golf":
            data[0].dislike = true;
            break;
          case "spa":
            data[1].dislike = true;
            break;
          case "beach":
            data[2].dislikes.push("BCH");
            break;
          case "ski":
            data[2].dislikes.push("WNTRACT");
            break;
          case "culinary":
            data[2].dislikes.push("COOK");
            break;
          case "culture":
            data[2].dislikes.push("CULTR");
            break;
          case "music":
            data[2].dislikes.push("MUSIC");
            break;
          default:
        }
      }
    });

    const actionUrl = actions["marriott-passions-v3/save-passions"];

    try {
      const response = await fetch(`${actionUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          acid: acid,
          passions: data,
          ...getExperienceParams(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to save passions: ${response.status} ${errorText}`,
        );
      }

      if (response.status === 204) {
        return {};
      }

      return await response.json();
    } catch (err) {
      console.error("Error in savePassionsToServer:", err);
      throw err; // Re-throw so calling code can handle
    }
  };

  return {
    selectPassion,
    unselectPassion,
    dislikePassion,
    tapPassion,
    isPassionSelected,
    isAnyPassionSelected,
    getPassionValue,
    getSelectedPassionsAsArray,
    getSelectedPassionsAsFlatArray,
    savePassionsToServer,
  };
};
